import { useEffect, useState } from "react";
import { Building2, FileText, CheckCircle2, XCircle, Loader2, ExternalLink } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const BADGE = {
  incomplet: "bg-amber-100 text-amber-700",
  en_attente: "bg-blue-100 text-blue-700",
  approuve: "bg-green-100 text-green-700",
  refuse: "bg-red-100 text-red-700",
};
const LABEL = { incomplet: "À compléter", en_attente: "En attente", approuve: "Approuvé", refuse: "Refusé" };
const DOCS = [{ k: "kbis", l: "Kbis" }, { k: "cni", l: "CNI gérant" }, { k: "rib", l: "RIB" }];

export default function AccountRequests() {
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [terms, setTerms] = useState("30j_fin_de_mois");
  const [limit, setLimit] = useState(1000);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/account-requests").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const decide = async (decision) => {
    setBusy(true);
    try {
      await api.put(`/account-requests/${sel.id}`, { decision, payment_terms: terms, credit_limit: Number(limit), note });
      toast.success(decision === "approuve" ? "Compte approuvé" : "Demande refusée");
      setSel(null); setNote(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  };

  const openDoc = (cid, k) => {
    const token = localStorage.getItem("aj_token");
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/account-requests/${cid}/documents/${k}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => window.open(URL.createObjectURL(b), "_blank"))
      .catch(() => toast.error("Document indisponible"));
  };

  return (
    <div data-testid="account-requests-page">
      <div className="flex items-center gap-2 mb-6"><Building2 className="text-brand" /><h1 className="font-display text-3xl text-forest">Comptes professionnels</h1></div>
      {rows.length === 0 ? <p className="text-stone">Aucune demande de compte pro.</p> : (
        <div className="grid gap-3">
          {rows.map((c) => (
            <div key={c.id} className="rounded-xl bg-white border border-border p-4 flex flex-wrap items-center justify-between gap-3" data-testid={`req-${c.id}`}>
              <div>
                <p className="font-semibold text-forest">{c.company || c.name} <span className="text-xs text-stone">· {c.account_type}</span></p>
                <p className="text-sm text-stone">{c.email} · {c.phone} {c.siret && `· SIRET ${c.siret}`}</p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${BADGE[c.account_status] || ""}`}>{LABEL[c.account_status] || c.account_status}</span>
                <button onClick={() => { setSel(c); setLimit(c.credit_limit || 1000); setTerms(c.payment_terms === "immediat" ? "30j_fin_de_mois" : (c.payment_terms || "30j_fin_de_mois")); }}
                  className="rounded-full bg-brand text-cream px-4 py-1.5 text-sm font-semibold hover:bg-forest" data-testid={`review-${c.id}`}>Examiner</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {sel && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSel(null)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
            <h2 className="font-display text-2xl text-forest mb-1">{sel.company || sel.name}</h2>
            <p className="text-sm text-stone mb-4">{sel.email} · {sel.phone} {sel.siret && `· SIRET ${sel.siret}`}</p>
            <p className="text-sm font-semibold text-forest mb-2">Documents</p>
            <div className="space-y-2 mb-5">
              {DOCS.map((d) => (
                <div key={d.k} className="flex items-center justify-between rounded-lg border border-border p-2.5">
                  <span className="text-sm text-forest flex items-center gap-2"><FileText size={15} className={sel.documents?.[d.k] ? "text-green-600" : "text-stone"} /> {d.l}</span>
                  {sel.documents?.[d.k]
                    ? <button onClick={() => openDoc(sel.id, d.k)} className="text-brand text-xs font-semibold flex items-center gap-1 hover:underline" data-testid={`view-${d.k}`}>Voir <ExternalLink size={12} /></button>
                    : <span className="text-xs text-stone">Manquant</span>}
                </div>
              ))}
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs text-stone">Conditions de paiement</label>
                <select value={terms} onChange={(e) => setTerms(e.target.value)} data-testid="terms-select" className="w-full rounded-lg border border-border px-3 py-2 text-sm">
                  <option value="immediat">Paiement à la commande (CB)</option>
                  <option value="30j_fin_de_mois">30 jours fin de mois</option>
                </select>
              </div>
              {terms === "30j_fin_de_mois" && (
                <div>
                  <label className="text-xs text-stone">Encours autorisé maximum (€ HT)</label>
                  <input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} data-testid="limit-input" className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
                </div>
              )}
              <div>
                <label className="text-xs text-stone">Note (envoyée au client)</label>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="decision-note" className="w-full rounded-lg border border-border px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => decide("refuse")} disabled={busy} data-testid="refuse-btn" className="flex-1 rounded-full border border-red-300 text-red-600 py-2.5 font-semibold hover:bg-red-50 flex items-center justify-center gap-2"><XCircle size={17} /> Refuser</button>
              <button onClick={() => decide("approuve")} disabled={busy} data-testid="approve-btn" className="flex-1 rounded-full bg-brand text-cream py-2.5 font-semibold hover:bg-forest flex items-center justify-center gap-2">{busy ? <Loader2 className="animate-spin" size={17} /> : <CheckCircle2 size={17} />} Approuver</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
